import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, Check, Sparkles,
  Trees, Coffee, ShoppingBag, Building2, Landmark, Castle,
  VolumeX, TrafficCone, MapPin, Camera, Waves, Store,
  Navigation as NavigationIcon, Loader2,
} from "lucide-react";
import { requestUserLocation } from "@/utils/geolocation";
import type { LucideIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { markSignInSkipped } from "@/components/QuickSignIn";
import { PleasantLogo } from "@/components/PleasantLogo";
import { fmtDistance } from "@/utils/units";
import { UNIT_SYSTEMS, UNIT_LABELS, getUnitSystem, setUnitSystem, suggestUnitSystem, type UnitSystem } from "@/utils/unitSettings";

import { PREF_LABELS, type Preference } from "@/utils/scoring";
import type { TravelMode } from "@/api/routing";
import {
  saveProfile, type UserProfile, type PleasantnessProfile, type DetourTolerance,
} from "@/utils/profile";
import { PleasantnessProfilePicker } from "@/components/PleasantnessProfilePicker";
import { DetourToleranceSlider } from "@/components/DetourToleranceSlider";
import { localBackroadSequence } from "@/api/pleasantWaypoints";
import type { LngLat } from "@/utils/polyline";
import { RoutePhoto } from "@/components/RoutePhoto";
// Landing-page route imagery for the SF → Half Moon Bay example trip.
// Real photographs of the two roads: Route 1 (the coastal CA-1 — the
// Pleasant pick) and Route 101 (the inland US-101 freeway — the fast pick).
// RoutePhoto falls back to its themed diagram if an image fails to load.
import fastShot from "@/assets/fast-route.jpg";
import pleasantShot from "@/assets/pleasant-route.jpg";
import { MAPBOX_TOKEN } from "@/lib/env";
import { supabase } from "@/integrations/supabase/client";
import { loadExamplePreview } from "@/utils/examplePreviewCache";

// Probes whether the keys required to render the real example are
// configured. Returns the list of human-readable missing key names so the
// setup screen can tell the user *exactly* what to add.
async function detectMissingExampleKeys(): Promise<string[]> {
  const missing: string[] = [];
  if (!MAPBOX_TOKEN) missing.push("VITE_MAPBOX_TOKEN");
  try {
    const { data, error } = await supabase.functions.invoke("google-directions", {
      body: {
        origin: [-122.3933, 37.7955],
        destination: [-122.3, 37.8],
        mode: "driving",
      },
    });
    const errMsg = (error?.message ?? "") + " " + JSON.stringify(data ?? {});
    if (/GOOGLE_PLACES_API_KEY/i.test(errMsg) || /not configured/i.test(errMsg)) {
      missing.push("GOOGLE_PLACES_API_KEY");
    }
  } catch {
    // Network errors are non-deterministic — don't guess. The real example
    // page itself will surface routing failures normally.
  }
  return missing;
}

interface Props {
  onComplete: (profile: UserProfile) => void;
}

// "Avoid highways" grouping (no-highways, no-traffic, quiet) intentionally
// removed from onboarding — pleasantness now focuses on positive attributes.
const ORDERED_PREFS: Preference[] = [
  "parks", "scenic", "monuments",
  "coffee", "shops", "architecture",
  "historic", "lively-streets", "coastal",
];

// Distinct icon + color per preference. Colors are tailwind utility classes
// so they pop against both the off-state card and the on-state pleasant gradient.
const PREF_META: Record<Preference, { Icon: LucideIcon; tint: string; onTint: string }> = {
  "parks":          { Icon: Trees,        tint: "bg-emerald-100 text-emerald-600",  onTint: "bg-emerald-400/30 text-white" },
  "coffee":         { Icon: Coffee,       tint: "bg-amber-100 text-amber-700",      onTint: "bg-amber-300/30 text-white" },
  "shops":          { Icon: ShoppingBag,  tint: "bg-pink-100 text-pink-600",        onTint: "bg-pink-300/30 text-white" },
  "architecture":   { Icon: Building2,    tint: "bg-indigo-100 text-indigo-600",    onTint: "bg-indigo-300/30 text-white" },
  "historic":       { Icon: Castle,       tint: "bg-orange-100 text-orange-600",    onTint: "bg-orange-300/30 text-white" },
  "monuments":      { Icon: Landmark,     tint: "bg-purple-100 text-purple-600",    onTint: "bg-purple-300/30 text-white" },
  "scenic":         { Icon: Camera,       tint: "bg-rose-100 text-rose-600",        onTint: "bg-rose-300/30 text-white" },
  "coastal":        { Icon: Waves,        tint: "bg-sky-100 text-sky-600",          onTint: "bg-sky-300/30 text-white" },
  "lively-streets": { Icon: Store,        tint: "bg-fuchsia-100 text-fuchsia-700",  onTint: "bg-fuchsia-300/30 text-white" },
  "quiet":          { Icon: VolumeX,      tint: "bg-teal-100 text-teal-600",        onTint: "bg-teal-300/30 text-white" },
  "no-traffic":     { Icon: TrafficCone,  tint: "bg-red-100 text-red-600",          onTint: "bg-red-300/30 text-white" },
  "no-highways":    { Icon: MapPin,       tint: "bg-yellow-100 text-yellow-700",    onTint: "bg-yellow-300/30 text-white" },
};

export const Onboarding = ({ onComplete }: Props) => {
  // Step 2 added in the first-60-seconds audit (#62) for location-
  // permission priming. Index.tsx's stepper dots still count three
  // visible positions because PermissionPrimingStep renders its own
  // header.
  // Onboarding is a single screen now (the demo). The old "What kind of trip?"
  // toggle page and the separate location-priming page were both removed to cut
  // clicks; community-trust stays on by default and "Start your trip" requests
  // location inline. `step` is kept only so the {step === 0 && …} wrapper holds.
  const [step] = useState<0>(0);
  const [loves, setLoves] = useState<Preference[]>(["scenic", "parks"]);
  const [mode] = useState<TravelMode>("walking");
  const prioritizeCommunity = true;
  const [pleasantnessProfile, setPleasantnessProfile] = useState<PleasantnessProfile>("nature");
  const [detourTolerance, setDetourTolerance] = useState<DetourTolerance>("balanced");
  const [exampleChecking, setExampleChecking] = useState(false);
  const [missingKeys, setMissingKeys] = useState<string[] | null>(null);
  const [locationRequesting, setLocationRequesting] = useState(false);
  // ASKED, NOT ASSUMED (Morgan, Aug 18). The phone's region only PRESELECTS
  // the answer, so most people confirm rather than choose; nothing is written
  // to storage until they press Start, which is what makes this an answer and
  // not a guess. The example distances below re-render as they tap, so the
  // question shows its own consequences instead of describing them.
  const [units, setUnits] = useState<UnitSystem>(() => getUnitSystem() ?? suggestUnitSystem());
  const unitsAlreadyChosen = useMemo(() => getUnitSystem() !== null, []);
  // The onboarding example is always the SF → Half Moon Bay drive. (The
  // walking/biking mode picker was removed — mode is chosen on the main
  // route screen.)
  const [exampleMode] = useState<TravelMode>("driving");

  const toggle = (p: Preference) =>
    setLoves((cur) => (cur.includes(p) ? cur.filter((x) => x !== p) : [...cur, p]));

  const finish = () => {
    // Always write it, even when it matches the suggestion: the stored value is
    // how the rest of the app knows this driver has been asked.
    setUnitSystem(units);
    const profile = saveProfile({
      loves, mode, prioritizeCommunity, pleasantnessProfile, detourTolerance,
    });
    onComplete(profile);
  };

  // Onboarding is a single screen now. The demo's "Start your trip" button
  // requests location and drops the user straight on the map — the old
  // separate location-priming page is gone. A permission failure never blocks
  // entry (they can always type a starting address in-app).
  const startTripAndEnter = async () => {
    if (locationRequesting) return;
    setLocationRequesting(true);
    try {
      await requestUserLocation({ timeoutMs: 8_000 });
    } catch {
      // ignore — the map works with a typed start address
    }
    setLocationRequesting(false);
    finish();
  };

  /** Run the real example route for the currently-selected travel mode. */
  const loadExample = async () => {
    if (exampleChecking) return;
    setExampleChecking(true);
    const missing = await detectMissingExampleKeys();
    if (missing.length > 0) {
      setMissingKeys(missing);
      setExampleChecking(false);
      return;
    }
    const examples: Record<TravelMode, { start: string; end: string }> = {
      driving: {
        start: "-122.3933,37.7955|San Francisco",
        end:   "-122.4286,37.4636|Half Moon Bay",
      },
      walking: {
        start: "-122.3933,37.7955|Ferry Building",
        end:   "-122.4662,37.8036|Crissy Field",
      },
      biking: {
        start: "-122.3933,37.7955|Ferry Building",
        end:   "-122.4604,37.7726|Conservatory of Flowers",
      },
    };
    const trip = examples[exampleMode];
    // Aug 19 units audit: this exit path saved the profile but never wrote the
    // units choice, so a driver who picked km and tapped the example card lost
    // the pick silently — the same write finish() does, for the same reason:
    // the stored value is how the rest of the app knows this driver has been
    // asked.
    setUnitSystem(units);
    // Default a first-time user coming through the example trip to "scenic"
    // tolerance, not "balanced." The whole point of the example is to show
    // the difference Pleasant makes — on Balanced the routing engine often
    // returns essentially Google's path because the tolerance for extra
    // time is too low. Scenic lets the engine commit to the coastal route
    // (CA-1) the example is specifically designed to showcase.
    //
    // The user can still change this from the on-map detour-tolerance pill
    // anytime; the coachmark below points to where.
    saveProfile({
      loves: ["scenic", "parks"],
      mode: exampleMode,
      prioritizeCommunity: true,
      pleasantnessProfile: "nature",
      detourTolerance: "scenic",
    });
    markSignInSkipped();
    // No `prefill=1` flag — when a user taps the example trip card we want
    // the route to actually GENERATE and render, not just pre-fill the
    // destination input and leave them staring at the inputs.
    //
    // Also flag the intro as dismissed for this session so Index.tsx doesn't
    // re-mount the Onboarding screen on top of the route after the redirect.
    // Real-world feedback: "this card should link to the actual route, right
    // now it just reloads the page." The reload was happening; what looked
    // like a reload was Onboarding rendering again because `introDismissed`
    // starts false on every fresh mount. Persisting it in sessionStorage
    // keeps "show intro on every fresh visit" intact while letting the
    // example card jump straight to the map.
    try { sessionStorage.setItem("pleasant.introDismissed", "1"); } catch { /* ignore */ }
    const params = new URLSearchParams({
      start: trip.start,
      end: trip.end,
      mode: exampleMode,
      // Flag so Index can show a one-time coachmark pointing at the
      // detour-tolerance pill — explains where the savorer can tune how
      // far Pleasant is willing to detour for a prettier route.
      onboarded: "1",
    });
    window.location.assign(`${window.location.pathname}?${params.toString()}`);
  };

  // Real example trip — SF (Embarcadero) → Half Moon Bay, CA, driving.
  // A commuter-scale coastal hop (~30 mi) where the pleasant pick adds only
  // a handful of minutes but trades 280/92 for the CA-1 coast. We compute
  // the actual matched coastal anchors via the same routing helper the live
  // engine uses (`localBackroadSequence`), so the segments and POIs listed
  // in the card match what the system will really pick.
  const EXAMPLE_START: LngLat = [-122.3933, 37.7955];
  const EXAMPLE_END:   LngLat = [-122.4286, 37.4636];
  const exampleMatch = useMemo(
    () => localBackroadSequence(EXAMPLE_START, EXAMPLE_END, "driving"),
    [],
  );
  // Each matched anchor is a CA-1 segment ("Pacifica (CA-1)", etc.) — split
  // off the parenthetical so the chip reads as a place name, and reuse the
  // anchor list as the POI list (anchors mark the actual scenic stops the
  // routing engine forces the route through).
  const matchedSegments = (exampleMatch?.waypoints ?? []).map((w) => ({
    label: (w.name ?? "").replace(/\s*\([^)]*\)\s*$/, "") || "Coastal anchor",
    kind: w.kind,
  }));
  const corridorLabel = exampleMatch?.label ?? "Pacific Coast Highway (CA-1)";
  const corridorReason = exampleMatch?.reason ?? "Coastal route along CA-1";

  const fmtMin = (s: number) => {
    const m = Math.round(s / 60);
    if (m < 60) return `${m} min`;
    const h = Math.floor(m / 60);
    const r = m % 60;
    return r ? `${h} hr ${r} min` : `${h} hr`;
  };
  // Was hardcoded miles. Passing `units` (not the stored value) means the
  // example cards change under the picker as the driver tries each option.
  const fmtMi = (m: number) => fmtDistance(m, units);

  // Real numbers from a previous run of the example route, if available.
  // We GUARD the cached values so a stale long-distance run (e.g. a previous
  // cross-state route in the cache) never lands here as "+121 min". The
  // onboarding example is an explicit commuter-scale hop (~30 mi, ~35 min);
  // anything that wildly exceeds that scale is rejected so the copy keeps
  // the small-trade framing the rest of the page promises.
  const rawExamplePreview = useMemo(() => loadExamplePreview(), []);
  const examplePreview = useMemo(() => {
    if (!rawExamplePreview) return null;
    const fastSec = rawExamplePreview.fastest.durationS;
    const pleasantSec = rawExamplePreview.pleasant.durationS;
    const fastMi = rawExamplePreview.fastest.distanceM / 1609.34;
    const pleasantMi = rawExamplePreview.pleasant.distanceM / 1609.34;
    const deltaMinCached = Math.round((pleasantSec - fastSec) / 60);
    const score = rawExamplePreview.pleasant.pleasantScore ?? 0;
    // Commuter-scale envelope for this specific example trip.
    const COMMUTER_OK =
      fastSec > 0 && fastSec <= 90 * 60 &&        // fastest under 90 min
      pleasantSec > 0 && pleasantSec <= 110 * 60 && // pleasant under 110 min
      fastMi <= 60 && pleasantMi <= 70 &&         // both under ~60–70 mi
      deltaMinCached >= 0 && deltaMinCached <= 25; // delta in commuter range
    // Quality guard: the example is meant to showcase the Pleasant pick.
    // Reject snapshots where the pleasant route degenerated to the fastest
    // (identical duration AND distance) or scored too low to be compelling
    // — these produce cards like "1hr 5min / 1hr 5min · Score 4/100" which
    // make the product look broken on the landing page.
    const SAME_AS_FASTEST =
      Math.abs(pleasantSec - fastSec) < 60 &&
      Math.abs(rawExamplePreview.pleasant.distanceM - rawExamplePreview.fastest.distanceM) < 500;
    const QUALITY_OK = score >= 35 && !SAME_AS_FASTEST;
    return COMMUTER_OK && QUALITY_OK ? rawExamplePreview : null;
  }, [rawExamplePreview]);

  // Walking-mode example: Ferry Building → Crissy Field. The numbers and
  // segment mileage below were measured from the same Mapbox/OSM pipeline
  // the live engine uses (see __tests__/walkingExampleVerification.test.ts),
  // so each Pleasant component matches what the scorer actually credits:
  //   • Embarcadero waterfront promenade — 1.6 mi (water-adjacent path)
  //   • Aquatic Park beach + promenade  — 0.4 mi (park + waterfront)
  //   • Fort Mason bayfront            — 0.5 mi (park + view)
  //   • Marina Green → Crissy Field     — 0.9 mi (park + waterfront)
  //   = 3.4 mi continuous waterfront, 0 arterial crossings vs ~12 inland.
  // Trade-off: +4 min over Google's Columbus/Bay/North Point inland line.
  const WALKING_DEFAULTS = {
    fastestMin: "1 hr 8 min",
    fastestMi:  "3.4 mi",
    pleasantMin: "1 hr 12 min",
    pleasantMi:  "3.5 mi",
    deltaMin: 4,
    corridorLabel: "Embarcadero → Crissy Field waterfront",
    corridorReason: "via a continuous bayfront promenade through Aquatic Park, Fort Mason, and Marina Green",
    components: [
      { label: "Embarcadero promenade",      detail: "1.6 mi waterfront, car-separated" },
      { label: "Aquatic Park",               detail: "0.4 mi park + beach" },
      { label: "Fort Mason bayfront",        detail: "0.5 mi park frontage, Bay views" },
      { label: "Marina Green → Crissy Field", detail: "0.9 mi shoreline promenade" },
    ],
    fastestSummary: "Inland through Columbus, Bay & North Point — ~12 crowded crossings, no water in sight.",
    pleasantSummary: "3.4 mi of pretty waterfront · 0 arterial crossings · Bay & Bridge views worth the walk.",
  } as const;

  // Fixed captions for the SF → Half Moon Bay example cards. The example
  // cards never change, so the copy under them doesn't either (real-drive
  // note: "no need for it to change if the cards stay the same"). Stale and
  // monotonous on the left; inspiring and new on the right.
  const FASTEST_CAPTION = "The same gray miles, every time.";
  const PLEASANT_CAPTION = "Something new around every bend.";

  const isWalking = exampleMode === "walking";

  // Driving fallbacks (shown only before a real snapshot exists) are kept
  // honest and CLOSE — the live SF → Half Moon Bay result comes back at
  // ~40 min (Direct) vs ~43 min (coastal CA-1), a ~3 min trade, not the old
  // 35-vs-46 (+11 min) overstatement. Making the demo match reality is both
  // truthful and a stronger pitch: a nicer way at about the same time.
  const fastestMin = isWalking
    ? WALKING_DEFAULTS.fastestMin
    : examplePreview ? fmtMin(examplePreview.fastest.durationS) : "40 min";
  const fastestMi  = isWalking
    ? WALKING_DEFAULTS.fastestMi
    : examplePreview ? fmtMi(examplePreview.fastest.distanceM) : fmtMi(48280);
  const pleasantMin = isWalking
    ? WALKING_DEFAULTS.pleasantMin
    : examplePreview ? fmtMin(examplePreview.pleasant.durationS) : "43 min";
  const pleasantMi  = isWalking
    ? WALKING_DEFAULTS.pleasantMi
    : examplePreview ? fmtMi(examplePreview.pleasant.distanceM) : fmtMi(46670);
  const pleasantScore = isWalking ? null : (examplePreview?.pleasant.pleasantScore ?? null);
  const pleasantReasons = isWalking ? [] : (examplePreview?.pleasant.reasons ?? []);
  const deltaMin = isWalking
    ? WALKING_DEFAULTS.deltaMin
    : examplePreview
    ? Math.max(0, Math.min(25, Math.round((examplePreview.pleasant.durationS - examplePreview.fastest.durationS) / 60)))
    : 3;

  return (
    <main className="min-h-screen bg-hero flex flex-col items-center justify-center px-4 sm:px-6 py-8 sm:py-10 relative overflow-hidden">
      {/* Decorative dotted trails */}
      <svg className="absolute -top-20 -right-20 w-[520px] h-[520px] opacity-[0.06] pointer-events-none" viewBox="0 0 200 200" fill="none">
        <path d="M40 180 Q 60 120, 100 110 T 160 30" stroke="hsl(var(--primary))" strokeWidth="2" strokeDasharray="2 6" />
      </svg>
      <svg className="absolute -bottom-20 -left-20 w-[460px] h-[460px] opacity-[0.06] pointer-events-none" viewBox="0 0 200 200" fill="none">
        <path d="M20 20 Q 80 60, 100 100 T 180 180" stroke="hsl(var(--mint))" strokeWidth="2" strokeDasharray="2 6" />
      </svg>

      <div className="relative z-10 w-full max-w-2xl animate-fade-up">
        <div className="flex justify-center mb-8">
          <PleasantLogo size="md" withPin />
        </div>

        {/* Progress dots — now 3 stops to reflect the location-permission
         *  priming step added in the first-60-seconds audit (#62). */}
        <div className="flex justify-center gap-2 mb-8" aria-hidden>
          {[0, 1, 2].map((i) => (
            <span
              key={i}
              className={`h-1.5 rounded-full transition-all ${
                i === step ? "w-10 bg-primary" : "w-6 bg-muted"
              }`}
            />
          ))}
        </div>

        {/* Step 0 — Welcome / Start your trip */}
        {step === 0 && (
          <section className="animate-fade-up text-center">
            <div className="flex justify-center mb-6 sm:mb-8">
              <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-pleasant flex items-center justify-center shadow-glow">
                <Sparkles className="w-8 h-8 sm:w-9 sm:h-9 text-primary-foreground" />
              </div>
            </div>

            {/* First-60-seconds audit (#62): "They want it fast. We want
             *  it nice." was clever ad copy, but a stranger has no
             *  antecedent for "they" on first read. Lands as a non-
             *  sequitur and costs 1-2 seconds of parsing. Replaced with
             *  a self-standing headline that promises the value
             *  directly. */}
            <h1 className="heading text-[2rem] sm:text-4xl md:text-5xl lg:text-6xl mb-4 leading-[1.1] tracking-tight text-balance px-1">
              A nicer way
              <br />
              <span className="text-primary">to where you're going.</span>
            </h1>

            <p className="text-muted-foreground mb-7 sm:mb-8 max-w-md md:max-w-xl mx-auto text-[15px] sm:text-base md:text-lg text-balance">
              Pretty streets and worthwhile views — not crowded highways and depressing strip malls.
            </p>

            {/* Real example trip — Maps fastest vs Pleasant */}
            <div className="max-w-md md:max-w-2xl mx-auto mb-6 text-left">
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mb-2 px-1">
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground min-w-0">
                  <MapPin className="w-3.5 h-3.5 text-primary flex-shrink-0" />
                  <span className="font-medium text-foreground truncate">
                    {isWalking ? "Ferry Building" : "San Francisco"}
                  </span>
                  <ArrowRight className="w-3 h-3 flex-shrink-0" />
                  <span className="font-medium text-foreground truncate">
                    {isWalking ? "Crissy Field" : "Half Moon Bay"}
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2.5 md:gap-4">
                {/* Maps card */}
                {(() => {
                  const fastestText = FASTEST_CAPTION;
                  const pleasantText = PLEASANT_CAPTION;
                  return (
                    <TooltipProvider delayDuration={150}>
                      <div className="rounded-2xl border border-border bg-card p-2.5 md:p-3.5 shadow-soft">
                        <div className="flex items-center justify-between mb-1.5">
                          <span
                            className="font-semibold uppercase tracking-wide text-muted-foreground"
                            style={{ fontSize: "clamp(0.625rem, 0.55rem + 0.4vw, 0.75rem)" }}
                          >Maps</span>
                          <span
                            className="font-medium text-muted-foreground"
                            style={{ fontSize: "clamp(0.625rem, 0.55rem + 0.4vw, 0.75rem)" }}
                          >Fastest</span>
                        </div>
                        <RoutePhoto
                          src={fastShot}
                          alt="US-101 — a wide, traffic-heavy inland freeway"
                          variant="freeway"
                        />
                        <div className="mt-2 md:mt-2.5 space-y-1">
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <p
                                className="font-medium text-foreground/90 leading-snug line-clamp-2 cursor-help min-h-[2.4em]"
                                style={{ fontSize: "clamp(0.75rem, 0.65rem + 0.55vw, 0.9375rem)" }}
                              >
                                {fastestText}
                              </p>
                            </TooltipTrigger>
                            <TooltipContent side="top" className="max-w-[260px] text-xs leading-snug">
                              {fastestText}
                            </TooltipContent>
                          </Tooltip>
                          <div className="flex items-baseline justify-between gap-2 pt-0.5">
                            <span
                              className="font-semibold text-muted-foreground tabular-nums"
                              style={{ fontSize: "clamp(0.6875rem, 0.6rem + 0.5vw, 0.875rem)" }}
                            >
                              ETA {fastestMin}
                            </span>
                            <span
                              className="text-muted-foreground tabular-nums"
                              style={{ fontSize: "clamp(0.6875rem, 0.6rem + 0.5vw, 0.875rem)" }}
                            >{fastestMi}</span>
                          </div>
                        </div>
                      </div>

                      <button
                        type="button"
                        onClick={loadExample}
                        disabled={exampleChecking}
                        aria-label={`Try the real ${exampleMode} example route`}
                        className="text-left rounded-2xl border-2 border-primary/40 bg-pleasant/10 p-2.5 md:p-3.5 shadow-glow hover:shadow-elevated hover:border-primary/70 active:scale-[0.99] transition-all disabled:opacity-70 disabled:cursor-wait focus:outline-none focus:ring-2 focus:ring-primary/60"
                      >
                        <div className="flex items-center justify-between mb-1.5">
                          <span
                            className="font-semibold uppercase tracking-wide text-primary"
                            style={{ fontSize: "clamp(0.625rem, 0.55rem + 0.4vw, 0.75rem)" }}
                          >Pleasant</span>
                          {pleasantScore !== null ? (
                            <span
                              className="font-semibold text-primary"
                              style={{ fontSize: "clamp(0.625rem, 0.55rem + 0.4vw, 0.75rem)" }}
                            >
                              {pleasantScore}/100
                            </span>
                          ) : (
                            <span
                              className="font-medium text-primary/80"
                              style={{ fontSize: "clamp(0.625rem, 0.55rem + 0.4vw, 0.75rem)" }}
                            >Best trip</span>
                          )}
                        </div>
                        <RoutePhoto
                          src={pleasantShot}
                          alt="Route 1 — the coastal highway winding above the ocean"
                          variant="coastal"
                        />
                        <div className="mt-2 md:mt-2.5 space-y-1">
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <p
                                className="font-medium text-foreground leading-snug line-clamp-2 cursor-help min-h-[2.4em]"
                                style={{ fontSize: "clamp(0.75rem, 0.65rem + 0.55vw, 0.9375rem)" }}
                              >
                                {pleasantText}
                              </p>
                            </TooltipTrigger>
                            <TooltipContent side="top" className="max-w-[260px] text-xs leading-snug">
                              {pleasantText}
                            </TooltipContent>
                          </Tooltip>
                          <div className="flex items-baseline justify-between gap-2 pt-0.5">
                            <span
                              className="font-semibold text-primary tabular-nums"
                              style={{ fontSize: "clamp(0.6875rem, 0.6rem + 0.5vw, 0.875rem)" }}
                            >
                              ETA {pleasantMin}
                            </span>
                            <span
                              className="text-muted-foreground tabular-nums"
                              style={{ fontSize: "clamp(0.6875rem, 0.6rem + 0.5vw, 0.875rem)" }}
                            >{pleasantMi}</span>
                          </div>
                          <p
                            className="text-primary/80 font-medium pt-1 line-clamp-1"
                            style={{ fontSize: "clamp(0.625rem, 0.55rem + 0.35vw, 0.75rem)" }}
                          >
                            {exampleChecking ? "Loading…" : `Tap to see the route on your map →`}
                          </p>
                        </div>
                      </button>
                    </TooltipProvider>
                  );
                })()}
              </div>

            </div>

            {/* Single primary CTA — About is reachable via "Read what this is"
             *  at the bottom of this screen, so having a second About button
             *  here was duplicative. Real-world feedback: "this should also
             *  not be here since there is also a link at the bottom of the
             *  start page." */}
            {/* DISTANCE UNITS — asked once, here, because this is the only
             *  screen every driver sees before their first route. Preselected
             *  from the phone's region; the cards above redraw as you tap, so
             *  the choice is visible rather than described. Hidden entirely
             *  for anyone who has already answered. */}
            {!unitsAlreadyChosen && (
              <div className="w-full max-w-sm mx-auto mb-4">
                <p className="text-xs text-muted-foreground mb-2">Show distances in</p>
                <div className="grid grid-cols-3 gap-1.5">
                  {UNIT_SYSTEMS.map((id) => (
                    <button
                      key={id}
                      type="button"
                      onClick={() => setUnits(id)}
                      aria-pressed={id === units}
                      className={`rounded-xl border px-2 py-2 text-center transition-smooth ${
                        id === units
                          ? "border-primary/60 bg-pleasant/10"
                          : "border-border bg-card hover:bg-muted/50"
                      }`}
                    >
                      <span className="block text-xs font-medium text-foreground">
                        {/* Aug 19 units audit: us and uk both said "miles",
                         *  making two of the three buttons indistinguishable —
                         *  the short unit is the difference worth showing. */}
                        {id === "metric" ? "km" : id === "uk" ? "miles + yards" : "miles + feet"}
                      </span>
                      <span className="block text-[10px] text-muted-foreground tabular-nums">
                        {fmtDistance(150, id)}
                      </span>
                    </button>
                  ))}
                </div>
                <p className="text-[11px] text-muted-foreground/80 mt-1.5">
                  {UNIT_LABELS[units].label} · change any time in settings
                </p>
              </div>
            )}

            <div className="w-full max-w-sm mx-auto">
              <Button
                onClick={startTripAndEnter}
                disabled={locationRequesting}
                className="w-full h-14 rounded-2xl bg-pleasant text-primary-foreground text-base font-semibold shadow-glow active:scale-[0.99] transition-transform disabled:opacity-70"
              >
                {locationRequesting ? "Starting…" : "Start your trip"}
                {!locationRequesting && <ArrowRight className="w-4 h-4 ml-2" />}
              </Button>
              <p className="text-center text-xs text-muted-foreground mt-2">
                Uses your location to begin — or type any starting address once you're in.
              </p>
            </div>

            {missingKeys && missingKeys.length > 0 && (
              <div className="mt-4 mx-auto max-w-md text-left rounded-2xl border border-border bg-card p-4 shadow-sm">
                <h3 className="text-sm font-semibold mb-1">Setup needed to load the real example</h3>
                <p className="text-xs text-muted-foreground mb-3">
                  The following key{missingKeys.length > 1 ? "s are" : " is"} not configured.
                  Add {missingKeys.length > 1 ? "them" : "it"} in Lovable Cloud → Settings, then try again.
                </p>
                <ul className="space-y-1.5 mb-3">
                  {missingKeys.map((k) => (
                    <li key={k} className="flex items-start gap-2 text-xs">
                      <span className="mt-1 w-1.5 h-1.5 rounded-full bg-destructive shrink-0" />
                      <div>
                        <code className="font-mono font-medium text-foreground">{k}</code>
                        <span className="text-muted-foreground ml-1.5">
                          {k === "VITE_MAPBOX_TOKEN"
                            ? "— map tiles & basemap (frontend env)"
                            : k === "GOOGLE_PLACES_API_KEY"
                            ? "— Google Directions comparison (backend secret)"
                            : ""}
                        </span>
                      </div>
                    </li>
                  ))}
                </ul>
                <button
                  type="button"
                  onClick={() => setMissingKeys(null)}
                  className="text-xs font-medium text-primary hover:underline"
                >
                  Dismiss
                </button>
              </div>
            )}


            <p className="text-xs text-muted-foreground mt-2 px-4">
              Free · No account · Walk, bike or drive
            </p>

            <p className="text-xs text-muted-foreground mt-4 px-4">
              New here?{" "}
              <Link
                to="/about"
                className="text-primary font-medium hover:underline"
              >
                Read what this is →
              </Link>
            </p>

            {/* Required-for-App-Store footer links: privacy + support reachable
             *  from the first screen, before sign-in or any data is collected.
             *  Kept visually quiet so the primary CTA above stays dominant. */}
            <p className="text-[11px] text-muted-foreground/80 mt-6 px-4 flex items-center justify-center gap-2 flex-wrap">
              <Link to="/privacy" className="hover:text-primary hover:underline transition-smooth">
                Privacy
              </Link>
              <span aria-hidden="true">·</span>
              <Link to="/support" className="hover:text-primary hover:underline transition-smooth">
                Support
              </Link>
              <span aria-hidden="true">·</span>
              <Link to="/about" className="hover:text-primary hover:underline transition-smooth">
                About
              </Link>
            </p>
          </section>
        )}

      </div>
    </main>
  );
};
